// Reusable multi-select for the shared per-org tag registry.
//
// Behaviour:
//   • Autocomplete over every existing tag, filtered by typed text.
//   • Selected tags render as coloured chips inside the input.
//   • Typing a name that doesn't exist and pressing Enter (or clicking
//     "Create <name>") POSTs a new tag with a randomly-picked colour
//     from the built-in palette, then selects it.
//   • Change fires on every add / remove. The parent is responsible
//     for persisting the id list via whichever API accepts `tag_ids`
//     (sites.update, locations.update, etc.).
//
// Design decisions:
//   • Colour on create is picked from a fixed palette so the tag list
//     stays visually varied without asking the admin to pick every
//     time. They can recolour later from Settings.
//   • Read-only mode hides the input and just renders the chips —
//     used in list rows / cards where mutation isn't wanted.

import { useMemo, useState } from 'react'
import {
  Autocomplete, Box, Chip, CircularProgress, TextField, Typography,
  createFilterOptions,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Tag, TagRef } from '@cmms/types'
import { ApiError } from '@cmms/api-client'

import { api } from '../lib/api'
import { toast } from '../store/snackbarStore'

// Rotating colour palette used for freshly-created tags. Balanced hue
// spread so consecutive creates don't collide visually.
const TAG_PALETTE = [
  '#3B82F6', // blue-500
  '#8B5CF6', // violet-500
  '#EC4899', // pink-500
  '#F97316', // orange-500
  '#F59E0B', // amber-500
  '#10B981', // emerald-500
  '#14B8A6', // teal-500
  '#0EA5E9', // sky-500
  '#EF4444', // red-500
  '#84CC16', // lime-500
]

type OptionShape = TagRef & { inputValue?: string }

const filter = createFilterOptions<OptionShape>({
  matchFrom: 'any',
  stringify: (option) => option.name,
})

interface Props {
  /** Currently-selected tag list. Parent supplies TagRefs (id/name/color). */
  value: TagRef[]
  /** Fires on every add / remove / create with the new full set. */
  onChange: (next: TagRef[]) => void
  /** Field label. Optional; defaults to "Tags". */
  label?: string
  /** Helper text shown below the field. */
  helperText?: string
  /** Read-only chip list — no input, no mutations. */
  readOnly?: boolean
  /** Compact chip size — good for embedding under a heading. */
  size?: 'small' | 'medium'
}

export function TagsField({
  value, onChange, label = 'Tags', helperText, readOnly, size = 'small',
}: Props) {
  const qc = useQueryClient()
  const tagsQ = useQuery({
    queryKey: ['tags'],
    queryFn: () => api.tags.list(),
    staleTime: 30_000,
    enabled: !readOnly,
  })
  const [inputValue, setInputValue] = useState('')

  const options: OptionShape[] = useMemo(() => {
    if (readOnly) return []
    return (tagsQ.data ?? []).map((t) => ({ id: t.id, name: t.name, color: t.color }))
  }, [tagsQ.data, readOnly])

  const createM = useMutation({
    mutationFn: async (name: string): Promise<Tag> => {
      // Pick a colour that isn't already the majority — cheap balance
      // rather than a strict "unused" search which requires N queries.
      const palette = TAG_PALETTE[Math.floor(Math.random() * TAG_PALETTE.length)]!
      return api.tags.create({ name, color: palette })
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['tags'] })
      onChange([...value, { id: created.id, name: created.name, color: created.color }])
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to create tag'),
  })

  // ── Read-only mode: just render the chips ─────────────────────
  if (readOnly) {
    if (value.length === 0) {
      return (
        <Typography sx={{ fontSize: 12, color: 'text.disabled', fontStyle: 'italic' }}>
          No tags
        </Typography>
      )
    }
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {value.map((t) => (
          <TagChip key={t.id} tag={t} size={size} />
        ))}
      </Box>
    )
  }

  return (
    <Autocomplete
      multiple
      size={size}
      value={value as OptionShape[]}
      onChange={(_, newValue) => {
        // `newValue` can be a mix of existing TagRefs and the sentinel
        // "create new" option (which carries `inputValue`).
        const last = newValue[newValue.length - 1]
        if (last && typeof last === 'object' && 'inputValue' in last && last.inputValue) {
          const name = last.inputValue.trim()
          if (name.length > 0) {
            createM.mutate(name)
          }
          return
        }
        onChange(newValue.map((o) => ({ id: o.id, name: o.name, color: o.color })))
      }}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      options={options}
      loading={tagsQ.isLoading}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params)
        const trimmed = params.inputValue.trim()
        const exists = opts.some((o) => o.name.toLowerCase() === trimmed.toLowerCase())
        if (trimmed.length > 0 && !exists) {
          filtered.push({
            id:         '__new__',
            name:       `Create "${trimmed}"`,
            color:      '#6B7280',
            inputValue: trimmed,
          })
        }
        return filtered
      }}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      freeSolo={false}
      renderOption={(props, option) => {
        const { key, ...rest } = props as { key: React.Key } & Record<string, unknown>
        const isNew = option.id === '__new__'
        return (
          <Box key={key} component="li" {...rest} sx={{ gap: 1 }}>
            {isNew ? (
              <>
                <Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: '#3B82F6', flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#3B82F6' }}>
                  {option.name}
                </Typography>
              </>
            ) : (
              <>
                <Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: option.color, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13 }}>{option.name}</Typography>
              </>
            )}
          </Box>
        )
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...rest } = getTagProps({ index })
          return (
            <Chip key={key} {...rest}
              label={option.name}
              size={size}
              sx={{
                bgcolor: `${option.color}22`,
                color: option.color,
                fontWeight: 700,
                borderColor: `${option.color}55`,
                border: '1px solid',
                '& .MuiChip-deleteIcon': { color: option.color, '&:hover': { color: option.color } },
              }} />
          )
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size={size}
          placeholder={value.length === 0 ? 'Add tags…' : ''}
          helperText={helperText ?? (createM.isPending ? 'Creating tag…' : ' ')}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {tagsQ.isLoading || createM.isPending
                  ? <CircularProgress size={16} sx={{ mr: 1 }} />
                  : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

/** Standalone coloured tag chip. Used by both TagsField (read-only)
 *  and any list/card that renders tag chips without the input. */
export function TagChip({ tag, size = 'small' }: { tag: TagRef; size?: 'small' | 'medium' }) {
  return (
    <Chip label={tag.name} size={size}
      sx={{
        height: size === 'small' ? 20 : 24,
        fontSize: size === 'small' ? 10.5 : 12,
        fontWeight: 700,
        bgcolor: `${tag.color}22`,
        color: tag.color,
        border: '1px solid',
        borderColor: `${tag.color}55`,
      }} />
  )
}
